import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ExternalLink, GripVertical, Loader2, Pencil, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { linksApi } from '@/api'
import { MAX_LINKS, type LinkItem } from '@/types/links'
import { cn } from '@/lib/utils'

interface Row extends LinkItem {
  _id: string
  /** vendor 是按域名带出来的（不是人填的）：改 URL 时跟着重新带。 */
  _autoVendor?: boolean
}

let seq = 0
const rowId = () => `link-${++seq}`

const toRows = (links: LinkItem[] | null | undefined): Row[] =>
  (links ?? []).map(l => ({ url: l.url, vendor: l.vendor ?? null, note: l.note ?? null, _id: rowId() }))

/** 只收 http / https（同后端 `LinkItem` 的校验）。 */
export function isValidLinkUrl(url: string): boolean {
  try {
    const u = new URL(url.trim())
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

/** 去空行、去首尾空格。返回 null 表示有不合法的 URL。 */
export function cleanLinks(rows: LinkItem[]): LinkItem[] | null {
  const out: LinkItem[] = []
  for (const r of rows) {
    const url = r.url.trim()
    if (!url) continue
    if (!isValidLinkUrl(url)) return null
    out.push({ url, vendor: r.vendor?.trim() || null, note: r.note?.trim() || null })
  }
  return out
}

const hostOf = (url: string) => {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
}

interface LinksEditorProps {
  value: LinkItem[]
  onChange: (next: LinkItem[]) => void
  disabled?: boolean
}

/**
 * 链接列表编辑器（产品 / 采购 / 物料申请共用）。顺序 = 优先，拖动排序；第一条是默认链接。
 * 输完 URL 调 `POST /common/links/vendor` 带出供应商名（只在人没填 vendor 时填）。
 * 受控组件：父组件持有 `LinkItem[]`，这里只多管一份行 id（拖动要稳定的 key）。
 */
export function LinksEditor({ value, onChange, disabled }: LinksEditorProps) {
  const { t } = useTranslation('productCatalog')
  const [rows, setRows] = useState<Row[]>(() => toRows(value))
  // 父组件把我们刚发出去的值传回来时不重建行（否则 id 变了、输入框失焦）
  const lastEmitted = useRef<string>(JSON.stringify(value ?? []))
  useEffect(() => {
    const incoming = JSON.stringify(value ?? [])
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming
      setRows(toRows(value))
    }
  }, [value])

  const emit = useCallback((next: Row[]) => {
    setRows(next)
    const plain = next.map(({ url, vendor, note }) => ({ url, vendor: vendor ?? null, note: note ?? null }))
    lastEmitted.current = JSON.stringify(plain)
    onChange(plain)
  }, [onChange])

  const [previewing, setPreviewing] = useState<Record<string, boolean>>({})
  // 刚加的行：URL 框拿焦点（点「Add link」后直接打字）
  // 打开时只有一行空的（详情页点 Add link）：它也拿焦点
  const [focusId, setFocusId] = useState<string | null>(() => (rows.length === 1 && !rows[0].url ? rows[0]._id : null))
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  const previewVendor = useCallback(async (id: string, url: string) => {
    if (!isValidLinkUrl(url)) return
    setPreviewing(p => ({ ...p, [id]: true }))
    try {
      const res = await linksApi.previewVendor(url.trim())
      const vendor = res?.vendor ?? null
      const cur = rowsRef.current
      const row = cur.find(r => r._id === id)
      // 预览回来时人已经填了 vendor，或 URL 又改了：不覆盖
      if (!row || row.url !== url || (row.vendor && !row._autoVendor)) return
      emit(cur.map(r => (r._id === id ? { ...r, vendor, _autoVendor: true } : r)))
    } catch {
      /* 预览失败不挡：后端写入时还会按域名补 */
    } finally {
      setPreviewing(p => ({ ...p, [id]: false }))
    }
  }, [emit])

  const update = (id: string, patch: Partial<Row>) =>
    emit(rows.map(r => (r._id === id ? { ...r, ...patch } : r)))

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = rows.findIndex(r => r._id === active.id)
    const to = rows.findIndex(r => r._id === over.id)
    if (from < 0 || to < 0) return
    emit(arrayMove(rows, from, to))
  }

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={rows.map(r => r._id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <SortableLinkRow
                  key={row._id}
                  row={row}
                  index={i}
                  disabled={disabled}
                  previewing={!!previewing[row._id]}
                  autoFocus={row._id === focusId}
                  onUrlChange={(url) => update(row._id, row._autoVendor ? { url, vendor: null } : { url })}
                  onUrlBlur={() => {
                    if (row.url.trim() && (!row.vendor || row._autoVendor)) void previewVendor(row._id, row.url)
                  }}
                  onVendorChange={(vendor) => update(row._id, { vendor: vendor || null, _autoVendor: false })}
                  onNoteChange={(note) => update(row._id, { note: note || null })}
                  onRemove={() => emit(rows.filter(r => r._id !== row._id))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={disabled || rows.length >= MAX_LINKS}
          onClick={() => {
            const id = rowId()
            setFocusId(id)
            emit([...rows, { url: '', vendor: null, note: null, _id: id }])
          }}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          {t('links.add')}
        </Button>
        {rows.length > 1 && <span className="text-[11px] text-muted-foreground">{t('links.dragHint')}</span>}
        {rows.length >= MAX_LINKS && <span className="text-[11px] text-muted-foreground">{t('links.max', { count: MAX_LINKS })}</span>}
      </div>
    </div>
  )
}

interface SortableLinkRowProps {
  row: Row
  index: number
  disabled?: boolean
  previewing: boolean
  autoFocus?: boolean
  onUrlChange: (v: string) => void
  onUrlBlur: () => void
  onVendorChange: (v: string) => void
  onNoteChange: (v: string) => void
  onRemove: () => void
}

function SortableLinkRow({ row, index, disabled, previewing, autoFocus, onUrlChange, onUrlBlur, onVendorChange, onNoteChange, onRemove }: SortableLinkRowProps) {
  const { t } = useTranslation('productCatalog')
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row._id, disabled })
  const invalid = row.url.trim() !== '' && !isValidLinkUrl(row.url)
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('rounded-md border border-border bg-card p-2', isDragging && 'z-10 shadow-md')}
      data-testid="link-row"
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className="flex h-7 w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground hover:text-foreground disabled:cursor-not-allowed"
          aria-label={t('links.dragHandle')}
          disabled={disabled}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Input
          value={row.url}
          onChange={e => onUrlChange(e.target.value)}
          onBlur={onUrlBlur}
          autoFocus={autoFocus}
          placeholder={t('links.urlPlaceholder')}
          disabled={disabled}
          className={cn('h-7 flex-1 text-xs', invalid && 'border-destructive')}
          aria-label={t('links.url')}
          aria-invalid={invalid}
        />
        {index === 0 && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t('links.default')}</span>}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label={t('links.remove')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 pl-6">
        <div className="relative w-36 shrink-0">
          <Input
            value={row.vendor ?? ''}
            onChange={e => onVendorChange(e.target.value)}
            placeholder={t('links.vendorPlaceholder')}
            disabled={disabled}
            className="h-7 pr-6 text-xs"
            aria-label={t('links.vendor')}
          />
          {previewing && <Loader2 className="absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 animate-spin text-muted-foreground" />}
        </div>
        <Input
          value={row.note ?? ''}
          onChange={e => onNoteChange(e.target.value)}
          placeholder={t('links.notePlaceholder')}
          disabled={disabled}
          className="h-7 flex-1 text-xs"
          aria-label={t('links.note')}
        />
      </div>
      {invalid && <div className="mt-1 pl-6 text-[11px] text-destructive">{t('links.invalidUrl')}</div>}
    </div>
  )
}

/** 只读列表：供应商名 + 域名，点开新窗口。第一条标「默认」。 */
export function LinksList({ links, className }: { links: LinkItem[] | null | undefined; className?: string }) {
  const { t } = useTranslation('productCatalog')
  if (!links?.length) return <div className={cn('text-sm text-muted-foreground', className)}>—</div>
  return (
    <ul className={cn('space-y-1', className)}>
      {links.map((l, i) => (
        <li key={`${l.url}-${i}`} className="flex min-w-0 items-center gap-1.5 text-sm">
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <a href={l.url} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate text-primary hover:underline" title={l.url}>
            {l.vendor || hostOf(l.url)}
          </a>
          {l.note && <span className="min-w-0 truncate text-xs text-muted-foreground">· {l.note}</span>}
          {i === 0 && links.length > 1 && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{t('links.default')}</span>}
        </li>
      ))}
    </ul>
  )
}

interface LinksFieldProps {
  links: LinkItem[] | null | undefined
  editable: boolean
  /** 整列替换。抛错 = 没存上（留在编辑态）。 */
  onSave: (next: LinkItem[]) => Promise<void>
}

/** 详情页用：只读列表 + 「编辑」→ 编辑器 + 保存 / 取消。 */
export function LinksField({ links, editable, onSave }: LinksFieldProps) {
  const { t } = useTranslation('productCatalog')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<LinkItem[]>([])
  const [saving, setSaving] = useState(false)
  const cleaned = cleanLinks(draft)

  if (!editing) {
    return (
      <div className="flex items-start gap-2">
        <LinksList links={links} className="min-w-0 flex-1" />
        {editable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs"
            onClick={() => { setDraft(links?.length ? links : [{ url: '', vendor: null, note: null }]); setEditing(true) }}
          >
            <Pencil className="mr-1 h-3 w-3" />
            {links?.length ? t('links.edit') : t('links.add')}
          </Button>
        )}
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <LinksEditor value={draft} onChange={setDraft} disabled={saving} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" disabled={saving} onClick={() => setEditing(false)}>
          {t('common:action.cancel')}
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 text-xs"
          disabled={saving || cleaned === null}
          onClick={async () => {
            if (!cleaned) return
            setSaving(true)
            try {
              await onSave(cleaned)
              setEditing(false)
            } catch { /* 调用方已提示 */ } finally {
              setSaving(false)
            }
          }}
        >
          {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
          {t('common:action.save')}
        </Button>
      </div>
    </div>
  )
}
